import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import * as Sentry from '@sentry/react';
import {useNavigate} from 'react-router-dom';
import {IV_PHOTO_SOURCE} from '@chekin/core';
import {UPLOAD_PHOTO_STATES} from './constants';
import {ANALYTIC_EVENTS, Analytics} from '../../analytics';
import {useErrorModal} from '../../hooks';
import {usePhotoBoxStore} from './store';
import {
  cn,
  MobileScreenshotPayLoad,
  VideoConstraints,
  WebcamRefTypes,
} from '@chekin/guestapp-ui-kit';
import {Buttons} from './components/Buttons';
import {UploadPhoto} from './components/UploadPhoto';
import {TakePhoto} from './components/TakePhoto';
import {ScanDocument} from './components/ScanDocument';
import {Wrapper} from './styled';

type PhotoBoxProps = {
  mobileHelperText?: ReactNode | string;
  mobileHelperTitle?: ReactNode | string;
  preloadedScreenshot?: string;
  placeholderImage?: string;
  onUploadPhoto?: (props: {image: string; source: IV_PHOTO_SOURCE}) => void;
  isUpload?: boolean;
  isOneSide?: boolean;
  roundedScanArea?: boolean;
  additionalConstraints?: VideoConstraints;
  isScan?: boolean;
  onScanDocument?: () => boolean;
  layoutMode?: string;
  className?: string;
};
const PhotoBox = ({
  isUpload,
  onUploadPhoto,
  placeholderImage,
  additionalConstraints,
  mobileHelperText,
  mobileHelperTitle,
  roundedScanArea,
  isScan,
  layoutMode = 'default',
  onScanDocument,
  className,
}: PhotoBoxProps) => {
  const webcamRef = useRef<WebcamRefTypes>(null);
  const uploadedImage = usePhotoBoxStore(state => state.uploadedImage);
  const setPhotoBoxUploadedImage = usePhotoBoxStore(state => state.setUploadedImage);
  const resetPhotoBoxUploadedImage = usePhotoBoxStore(state => state.resetUploadedImage);
  const [disabled, setDisabled] = useState(false);
  const {displayError, ErrorModal} = useErrorModal();
  const navigate = useNavigate();

  const uploadPhotoState = (() => {
    if (isUpload) {
      return UPLOAD_PHOTO_STATES.upload;
    } else if (uploadedImage && !isUpload) {
      return UPLOAD_PHOTO_STATES.confirmTaking;
    } else if (isScan && !isUpload) {
      return UPLOAD_PHOTO_STATES.scan;
    } else {
      return UPLOAD_PHOTO_STATES.take;
    }
  })();

  useEffect(
    function resetImageWhenLeavePage() {
      return () => {
        resetPhotoBoxUploadedImage();
      };
    },
    [resetPhotoBoxUploadedImage],
  );

  const handleScanDocument = useCallback(async () => {
    try {
      Analytics.track(ANALYTIC_EVENTS.ivPhotoTaken, {
        'Used camera': 'app',
      });

      onScanDocument?.();
    } catch (err) {
      Analytics.track(ANALYTIC_EVENTS.ivTakingPhotoError, {
        'Error message': (err as Error)?.message,
      });
      Sentry.captureException(err);
      displayError(err);
    }
  }, [displayError, onScanDocument]);

  const handleTakePhoto = useCallback(
    async (payload: MobileScreenshotPayLoad) => {
      try {
        Analytics.track(ANALYTIC_EVENTS.ivPhotoTaken, {
          'Used camera': payload.isNative ? 'native' : 'app',
        });

        const image = payload.isNative
          ? await payload.photo
          : await webcamRef?.current?.takePhoto();

        if (image)
          onUploadPhoto?.({
            image,
            source: payload.isNative
              ? IV_PHOTO_SOURCE.nativeCamera
              : IV_PHOTO_SOURCE.appCamera,
          });
      } catch (err) {
        Analytics.track(ANALYTIC_EVENTS.ivTakingPhotoError, {
          'Error message': (err as Error)?.message,
        });
        Sentry.captureException(err);
        displayError(err);
      }
    },
    [displayError, onUploadPhoto],
  );

  const handleUploadPhoto = useCallback(
    (image: string) => {
      setPhotoBoxUploadedImage(image);
    },
    [setPhotoBoxUploadedImage],
  );

  const handleChangePhoto = useCallback(() => {
    resetPhotoBoxUploadedImage();
    Analytics.track(ANALYTIC_EVENTS.ivResetPhoto);
  }, [resetPhotoBoxUploadedImage]);

  const handleFinishUploadPhoto = useCallback(() => {
    if (!uploadedImage) return;
    Analytics.track(ANALYTIC_EVENTS.ivPhotoUploaded);
    onUploadPhoto?.({image: uploadedImage, source: IV_PHOTO_SOURCE.external});
  }, [onUploadPhoto, uploadedImage]);

  const getContent = useCallback(() => {
    if (isScan) {
      return <ScanDocument />;
    }
    if (isUpload) {
      return (
        <UploadPhoto
          onUpload={handleUploadPhoto}
          onDisabled={setDisabled}
          image={uploadedImage}
        />
      );
    }

    return (
      <TakePhoto
        ref={webcamRef}
        disabled={disabled}
        onDisabled={setDisabled}
        roundedScanArea={roundedScanArea}
        additionalConstraints={additionalConstraints}
        onScreenshot={handleTakePhoto}
        placeholderImage={placeholderImage}
        mobileHelperText={mobileHelperText}
        mobileHelperTitle={mobileHelperTitle}
        mirrored={roundedScanArea}
        layoutMode={layoutMode}
        onBack={() => navigate(-1)}
      />
    );
  }, [
    isUpload,
    navigate,
    layoutMode,
    roundedScanArea,
    additionalConstraints,
    handleTakePhoto,
    placeholderImage,
    mobileHelperText,
    mobileHelperTitle,
    handleUploadPhoto,
    uploadedImage,
    setDisabled,
    disabled,
    isScan,
  ]);

  return (
    <Wrapper className={cn('photo-box', className)}>
      {getContent()}
      <Buttons
        state={uploadPhotoState}
        onCapture={() => {
          if (uploadPhotoState === UPLOAD_PHOTO_STATES.scan) {
            handleScanDocument();
          } else {
            handleTakePhoto({isNative: false});
          }
        }}
        onChange={handleChangePhoto}
        disabled={disabled}
        onFinishUploadPhoto={handleFinishUploadPhoto}
      />
      <ErrorModal />
    </Wrapper>
  );
};

export {PhotoBox};
